import { Link } from '@tanstack/react-router';
import { ArrowRight, ShieldOff } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardDescription, CardTitle } from '@/components/Card';
import { modules } from '@/lib/doctypes';
import { useMyPermissions } from '@/lib/permissions';

export function ModuleLanding({ modulePath }: { modulePath: string }) {
  const mod = modules.find((m) => m.path === modulePath);
  const perms = useMyPermissions();
  if (!mod) return null;

  // Filter the module's doctype cards by read permission. The sidebar
  // already hides modules with zero readable doctypes; this is the
  // belt-and-braces version for when a user navigates directly.
  const visible = mod.doctypes.filter((dt) => perms.canRead(dt.doctype));

  return (
    <>
      <PageHeader
        title={mod.name}
        subtitle={mod.description}
      />
      <div className="flex-1 px-4 sm:px-6 lg:px-8 pt-6 pb-8">
        {visible.length === 0 && !mod.links?.length ? (
          <Card className="max-w-xl">
            <div className="flex items-start gap-3">
              <ShieldOff className="size-5 text-stone shrink-0 mt-0.5" />
              <div>
                <CardTitle>No access</CardTitle>
                <CardDescription>
                  Your role doesn't include read permission for any document in {mod.name}.
                  Ask an administrator to grant access in Settings → Roles &amp; permissions.
                </CardDescription>
              </div>
            </div>
          </Card>
        ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {(mod.links ?? []).map((link) => {
            const Icon = link.icon;
            return (
              <Link
                key={link.to}
                to={link.to as never}
                className="block h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 rounded-lg"
              >
                <Card className="group h-full flex flex-col hover:border-border-strong hover:shadow-overlay transition-all cursor-pointer">
                  <div className="flex items-start justify-between mb-3">
                    <div className="inline-flex size-10 items-center justify-center rounded-lg bg-surface text-brand-green-ink group-hover:bg-brand-green-deep group-hover:text-white transition-colors">
                      <Icon className="size-5" />
                    </div>
                    <ArrowRight className="size-4 text-text-tertiary group-hover:text-accent group-hover:translate-x-0.5 transition-all" />
                  </div>
                  <CardTitle>{link.title}</CardTitle>
                  <CardDescription className="line-clamp-2">{link.description}</CardDescription>
                </Card>
              </Link>
            );
          })}
          {visible.map((dt) => {
            const Icon = dt.icon;
            return (
              <Link
                key={dt.slug + dt.modulePath}
                to={`${dt.modulePath}/${dt.slug}` as never}
                className="block h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 rounded-lg"
              >
                <Card className="group h-full flex flex-col hover:border-border-strong hover:shadow-overlay transition-all cursor-pointer">
                  <div className="flex items-start justify-between mb-3">
                    <div className="inline-flex size-10 items-center justify-center rounded-lg bg-surface text-brand-green-ink group-hover:bg-brand-green-deep group-hover:text-white transition-colors">
                      <Icon className="size-5" />
                    </div>
                    <ArrowRight className="size-4 text-text-tertiary group-hover:text-accent group-hover:translate-x-0.5 transition-all" />
                  </div>
                  <CardTitle>{dt.title}</CardTitle>
                  <CardDescription className="line-clamp-2">Open the {dt.singular.toLowerCase()} list.</CardDescription>
                </Card>
              </Link>
            );
          })}
        </div>
        )}
      </div>
    </>
  );
}
